import {
  SessionManager,
  sessionService,
  LoggerUtility,
  ApiManager
} from '../components/ComponentsLibrary';
import { ClientType } from '../enums/ClientType';

class SessionTimeoutService {
  constructor() {
    // Dynamic values from database
    this.TOTAL_TIMEOUT = null;
    this.WARNING_TIME = null;
    this.dynamicWarningMinutes = null;
    this.dynamicMessageContent = null;
    
    // Timers
    this.warningTimer = null;
    this.logoutTimer = null;
    this.countdownTimer = null;
    
    // State
    this.isWarningShown = false;
    this.countdownMinutes = 5;
    
    // Callbacks
    this.onShowWarning = null;
    this.onHideWarning = null;
    this.onCountdownUpdate = null;
    this.onLogout = null;
    
    // Activity events to track
    this.activityEvents = [
    'mousedown', 'keypress', 'scroll', 'touchstart', 'click'
    ];
    
    // Bind methods
    this.handleActivity = this.handleActivity.bind(this);
}
  
  /**
   * Initialize the session timeout monitoring
   */
  async initialize(callbacks) {
    try {
      this.onShowWarning = callbacks.onShowWarning;
      this.onHideWarning = callbacks.onHideWarning;
      this.onCountdownUpdate = callbacks.onCountdownUpdate;
      this.onLogout = callbacks.onLogout;
      
      await this.loadDynamicSettings();
      
      this.startActivityTracking();
      this.resetTimers();
      
      LoggerUtility.LogInfo('SessionTimeoutService', 'initialize', 'Session timeout monitoring started');
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'initialize');
    }
  }
  
  /**
   * Load dynamic settings from database
   */
  async loadDynamicSettings() {
    try {
      await Promise.all([
        this.loadWarningTimeFromSettings(),
        this.loadMessageFromContent()
      ]);
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'loadDynamicSettings');
    }
  }
  
  /**
   * Load warning time from SettingsDetail
   */
  async loadWarningTimeFromSettings() {
    try {
      const loginUserInfo = sessionService.getUser();
      const params = {
        settingsDetailIDList: ClientType.SettingsDetail.UI_Miscellaneous__Inactivity_Notification_Minutes.toString(),
        yTracSiteID: loginUserInfo?.siteID
      };
      
      const result = await ApiManager.GetSettingsDetailValuesFromApi(params);
      const settingValue = result?.settingDetailsApiData?.[0]?.settingsDetailValue;
      
      this.dynamicWarningMinutes = parseInt(settingValue);
      this.WARNING_TIME = this.dynamicWarningMinutes * 60 * 1000;
      this.TOTAL_TIMEOUT = (this.dynamicWarningMinutes + 5) * 60 * 1000;
      
      LoggerUtility.LogInfo('SessionTimeoutService', 'loadWarningTimeFromSettings', 
        `Loaded warning time: ${this.dynamicWarningMinutes} minutes`);
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'loadWarningTimeFromSettings');
    }
  }
  
  /**
   * Load message content from MessageContent table
   */
  async loadMessageFromContent() {
    try {
      const loginUserInfo = sessionService.getUser();
      const params = {
        messageContentID: ClientType.MessageContent.N_UI7055_Inactive_User,
        stateID: loginUserInfo?.stateID
      };
      
      const messageData = await ApiManager.getMessageContent(params);
      
      this.dynamicMessageContent = {
        NotificationType: "warning",
        Title: "Session Timeout Warning", 
        Message: messageData[0].messageText
      };
      
      LoggerUtility.LogInfo('SessionTimeoutService', 'loadMessageFromContent', 'Loaded message content from database');
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'loadMessageFromContent');
    }
  }
  
  /**
   * Start tracking user activity
   */
  startActivityTracking() {
    try {
      this.activityEvents.forEach(event => {
        document.addEventListener(event, this.handleActivity, true);
      });
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'startActivityTracking');
    }
  }
  
  /**
   * Handle user activity - reset timers
   */
  handleActivity(event) {
    try {
      // If warning is shown, ignore activity (let user interact with dialog)
      if (this.isWarningShown) {
        return;
      }
      
      // Reset all timers for normal activity
      this.resetTimers();
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'handleActivity');
    }
  }
  
  /**
   * Extend the session - called when user clicks "Extend Session" button
   */
  extendSession() {
    try {
      // Hide warning and reset all timers
      this.hideWarning();
      this.resetTimers();
      
      LoggerUtility.LogInfo('SessionTimeoutService', 'extendSession', 'User manually extended session');
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'extendSession');
    }
  }
  
  /**
   * Reset the timeout timers
   */
  resetTimers() {
    try {
      this.clearTimers();
      
      // Set timer to show warning at 55 minutes
      this.warningTimer = setTimeout(() => {
        this.showWarning();
      }, this.WARNING_TIME);
      
      // Set timer for logout at 60 minutes
      this.logoutTimer = setTimeout(() => {
        this.performLogout();
      }, this.TOTAL_TIMEOUT);
      
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'resetTimers');
    }
  }
  
  /**
   * Show the timeout warning
   */
  showWarning() {
    try {
      this.isWarningShown = true;
      this.countdownMinutes = 5;
      
      // Start countdown
      this.startCountdown();
      
      // Show warning dialog with dynamic message replacement
      if (this.onShowWarning) {
        const messageWithReplacements = {
          ...this.dynamicMessageContent,
          Message: this.dynamicMessageContent.Message
            .replace('{0}', this.dynamicWarningMinutes)
            .replace('{1}', this.countdownMinutes)
        };
        this.onShowWarning(messageWithReplacements);
      }
      
      LoggerUtility.LogWarning('SessionTimeoutService', 'showWarning', 'Session timeout warning displayed');
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'showWarning');
    }
  }
  
  /**
   * Hide the warning
   */
  hideWarning() {
    try {
      this.isWarningShown = false;
      this.clearCountdown();
      
      if (this.onHideWarning) {
        this.onHideWarning();
      }
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'hideWarning');
    }
  }
  
  /**
   * Start countdown timer
   */
  startCountdown() {
    try {
      this.countdownTimer = setInterval(() => {
        this.countdownMinutes--;
        
        if (this.countdownMinutes <= 0) {
          this.performLogout();
          return;
        }        
        // Update countdown in UI with current countdown value
        if (this.onCountdownUpdate) {
          const messageWithReplacements = {
            ...this.dynamicMessageContent,
            Message: this.dynamicMessageContent.Message
              .replace('{0}', this.dynamicWarningMinutes)
              .replace('{1}', this.countdownMinutes)
          };
          this.onCountdownUpdate(messageWithReplacements);
        }      }, 60000); 
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'startCountdown');
    }
  }
  
  /**
   * Clear countdown timer
   */
  clearCountdown() {
    try {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'clearCountdown');
    }
  }
  
  /**
   * Perform logout
   */
  performLogout() {
    try {
      this.cleanup();
      
      // Clear session
      sessionService.clearUser();
      SessionManager.ClearSession();
      
      // Trigger logout callback
      if (this.onLogout) {
        this.onLogout();
      }
      
      LoggerUtility.LogWarning('SessionTimeoutService', 'performLogout', 'User logged out due to inactivity');
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'performLogout');
    }
  }
  
  /**
   * Clear all timers
   */
  clearTimers() {
    try {
      if (this.warningTimer) {
        clearTimeout(this.warningTimer);
        this.warningTimer = null;
      }
      
      if (this.logoutTimer) {
        clearTimeout(this.logoutTimer);
        this.logoutTimer = null;
      }
      
      this.clearCountdown();
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'clearTimers');
    }
  }
  
  /**
   * Cleanup - remove listeners and clear timers
   */
  cleanup() {
    try {
      this.clearTimers();
      
      // Remove event listeners
      this.activityEvents.forEach(event => {
        document.removeEventListener(event, this.handleActivity, true);
      });
      
      this.isWarningShown = false;
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutService', 'cleanup');
    }
  }
}

// Create singleton instance
const sessionTimeoutService = new SessionTimeoutService();

export default sessionTimeoutService;